import { initializeApp } from "firebase/app";
import "firebase/auth";
import {
	applyActionCode,
	Auth,
	ConfirmationResult,
	confirmPasswordReset,
	createUserWithEmailAndPassword,
	FacebookAuthProvider,
	User as FirebaseUser,
	getAdditionalUserInfo,
	getAuth,
	GoogleAuthProvider,
	linkWithCredential,
	OAuthProvider,
	PhoneAuthProvider,
	RecaptchaVerifier,
	sendEmailVerification,
	sendPasswordResetEmail,
	signInWithEmailAndPassword,
	signInWithPhoneNumber,
	signInWithPopup,
	updatePhoneNumber,
	UserCredential,
} from "firebase/auth";
import { Observable, WritableObservable } from "micro-observables";
import { Signal } from "micro-signals";
import { Environment } from "@core/config/environment";
import { LoginError, RegisterError } from "./authError";
import { SocialAuthProvider } from "./authProvider";
import { AuthService } from "./authService";

export enum AuthResult {
	Success,
	Error,
	Canceled,
}

const FIREBASE_LOGGED_COOKIE = "firebase_logged";
export class BrowserAuthService implements AuthService {
	private firebaseAuth: Auth;
	private _firebaseUser: WritableObservable<FirebaseUser | null>;
	private phoneRecaptchaVerifier?: RecaptchaVerifier;
	private phoneRecaptchaHost?: HTMLDivElement;

	onFirebaseUserChange = new Signal<FirebaseUser | null>();

	get firebaseUser(): Observable<FirebaseUser | null> {
		return this._firebaseUser.readOnly();
	}

	get hasUser(): Observable<boolean> {
		return this._firebaseUser.transform((u) => !!u);
	}

	constructor() {
		const firebaseConfig = {
			apiKey: Environment.FIREBASE_API_KEY,
			authDomain: Environment.FIREBASE_AUTH_DOMAIN,
			databaseURL: Environment.FIREBASE_DATABASE_URL,
			projectId: Environment.FIREBASE_PROJECT_ID,
			storageBucket: Environment.FIREBASE_STORAGE_BUCKET,
			messagingSenderId: Environment.FIREBASE_MESSENGING_SENDER_ID,
			appId: Environment.FIREBASE_APP_ID,
		};
		initializeApp(firebaseConfig);
		this.firebaseAuth = getAuth();
		this.firebaseAuth.languageCode = "fr";
		this._firebaseUser = new WritableObservable(this.firebaseAuth.currentUser);
		this.firebaseAuth.onIdTokenChanged((user) => {
			this._firebaseUser.set(user);
			this.onFirebaseUserChange.dispatch(user);
		});
		this.firebaseAuth.onAuthStateChanged(
			(user) => {
				this._firebaseUser.set(user);
				this.updateLoggedCookie();
				this.onFirebaseUserChange.dispatch(user);
			},
			() => {
				this._firebaseUser.set(null);
				this.onFirebaseUserChange.dispatch(null);
			}
		);
	}

	async sendRecoveryEmail(email: string): Promise<void> {
		return sendPasswordResetEmail(this.firebaseAuth, email);
	}
	async sendVerifyEmail(): Promise<void> {
		const user = this.firebaseUser.get();
		if (!user) throw new Error("User not found");
		return sendEmailVerification(user);
	}

	async resetPassword(code: string, newPassword: string): Promise<void> {
		return confirmPasswordReset(this.firebaseAuth, code, newPassword);
	}
	async verify(code: string): Promise<void> {
		await applyActionCode(this.firebaseAuth, code);
		await this.refreshToken();
	}

	async checkUserVerified(): Promise<boolean> {
		await this._firebaseUser.get()?.reload();
		if (this._firebaseUser.get()?.emailVerified) {
			await this.refreshToken();
			return true;
		}
		return false;
	}

	async register(email: string, password: string): Promise<FirebaseUser | null> {
		try {
			return (await createUserWithEmailAndPassword(this.firebaseAuth, email, password)).user;
		} catch (e) {
			if (
				e instanceof Error &&
				"code" in e &&
				(e.code === "auth/email-already-exists" || e.code === "auth/email-already-in-use")
			) {
				throw Error(RegisterError.EXISTS, { cause: e });
			}
			throw Error(RegisterError.UNKNOWN, { cause: e });
		}
	}

	async login(email: string, password: string): Promise<void> {
		await this.firebaseAuth.signOut();
		try {
			await signInWithEmailAndPassword(this.firebaseAuth, email, password);
		} catch (e) {
			if (e instanceof Error && "code" in e && typeof e.code === "string" && isPasswordError(e.code)) {
				throw Error(LoginError.CREDENTIALS, { cause: e });
			}
			throw Error(LoginError.UNKNOWN, { cause: e });
		}
	}

	async setupRecaptcha(number: string): Promise<{ confirm: ConfirmationResult; recaptcha: RecaptchaVerifier }> {
		this.releasePhoneRecaptcha();
		const host = document.createElement("div");
		document.body.appendChild(host);
		this.phoneRecaptchaHost = host;
		const recaptcha = new RecaptchaVerifier(this.firebaseAuth, host, { size: "invisible" });
		this.phoneRecaptchaVerifier = recaptcha;
		try {
			await recaptcha.render();
			const confirm = await signInWithPhoneNumber(this.firebaseAuth, number, recaptcha);
			return { confirm, recaptcha };
		} catch (error) {
			this.releasePhoneRecaptcha();
			throw error;
		}
	}

	private releasePhoneRecaptcha(): void {
		const verifier = this.phoneRecaptchaVerifier;
		this.phoneRecaptchaVerifier = undefined;
		if (verifier) {
			try {
				verifier.clear();
			} catch {
				// The caller may already have cleared the widget.
			}
		}
		this.phoneRecaptchaHost?.remove();
		this.phoneRecaptchaHost = undefined;
	}

	async linkPhoneNumber(confirm: ConfirmationResult, code: string): Promise<UserCredential | undefined> {
		const credential = PhoneAuthProvider.credential(confirm.verificationId, code);

		const user = this.firebaseUser.get();
		if (!user) throw new Error("User not found");
		try {
			return await linkWithCredential(user, credential);
		} catch (error) {
			if (error instanceof Error && "code" in error && error.code === "auth/provider-already-linked") {
				throw Error(RegisterError.EXISTS, { cause: error });
			}
			console.error(error);
			return undefined;
		}
	}

	async updatePhoneNumber(confirm: ConfirmationResult, code: string): Promise<boolean | undefined> {
		const credential = PhoneAuthProvider.credential(confirm.verificationId, code);
		const user = this.firebaseUser.get();
		if (!user) throw new Error("User not found");
		try {
			await updatePhoneNumber(user, credential);
			return true;
		} catch (error) {
			if (error instanceof Error && "code" in error && error.code === "auth/provider-already-linked") {
				throw Error(RegisterError.EXISTS, { cause: error });
			}
			return undefined;
		}
	}

	async auth(authProvider: SocialAuthProvider): Promise<
		| {
				email?: string | null;
				displayName?: string | null;
				isNewUser?: boolean;
				emailVerified?: boolean;
		  }
		| undefined
	> {
		await this.firebaseAuth.signOut();
		try {
			const result = await signInWithPopup(
				this.firebaseAuth,
				authProvider === "Facebook"
					? new FacebookAuthProvider()
					: authProvider === "Google"
						? new GoogleAuthProvider()
						: new OAuthProvider("apple.com")
			);
			const additionalUserInfo = getAdditionalUserInfo(result);
			return {
				...result.user,
				isNewUser: additionalUserInfo?.isNewUser,
			};
		} catch (e) {
			if (
				e instanceof Error &&
				"code" in e &&
				typeof e.code === "string" &&
				(e.code === "auth/email-already-exists" || e.code === "auth/email-already-in-use")
			) {
				throw Error(RegisterError.EXISTS, { cause: e });
			}
			if (e instanceof Error && "code" in e && typeof e.code === "string" && e.code !== "auth/popup-closed-by-user") {
				throw Error(RegisterError.UNKNOWN, { cause: e });
			}
			return undefined;
		}
	}

	signOut(): Promise<void> {
		return this.firebaseAuth.signOut();
	}

	async getToken(): Promise<string | undefined> {
		return this._firebaseUser.get()?.getIdToken();
	}

	async refreshToken(): Promise<unknown> {
		return this._firebaseUser.get()?.getIdToken(true);
	}

	updateLoggedCookie(): void {
		const logged = this.hasUser.get();
		if (logged) {
			document.cookie = `${FIREBASE_LOGGED_COOKIE}=true; Path=/; Secure; SameSite=Lax; Max-Age=2592000`;
		} else {
			document.cookie = `${FIREBASE_LOGGED_COOKIE}=; Path=/; Secure; SameSite=Lax; Max-Age=0`;
		}
	}
}

function isPasswordError(code: string): boolean {
	return ["auth/invalid-password", "auth/wrong-password", "auth/user-not-found"].includes(code);
}
